import type { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { ReactNode } from 'react';

import { ConfigService } from '../../../../services/config';
import { getMonetaryColumnDescription, memoizedFormatDate } from '../../../../shared';
import { DateExplorerLink } from '../../../DataExplorerLink';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Stack, Tooltip, styled } from '@mui/material';
import Paper from '@mui/material/Paper';
import { PaymentCheck } from '../../../../__generated-global__';
import { UTCFormat } from '../../../../shared/time-utils';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

export const paymentCheckDisplayColumns: GridColDef[] = [
  {
    field: 'source',
    headerName: 'Source',
    width: 250,
    sortable: false,
    filterable: false,
  },
  {
    field: 'postedAt',
    headerName: 'Date',
    width: 110,
    sortable: true,
    filterable: false,
    valueGetter: (params: GridValueGetterParams<PaymentCheck>) => {
      return UTCFormat(params.row.postedAt, ConfigService.getInstance().getOrFail('DATE_FORMAT'));
    },
  },
  {
    field: 'amount',
    headerName: 'Amount',
    sortable: true,
    filterable: false,
    width: 200,
    renderCell: (params: GridRenderCellParams): ReactNode => {
      const { amount, amountInHomeCurrency, currency } = params.row;
      const isForex = currency !== 'USD';
      return getMonetaryColumnDescription(amount, amountInHomeCurrency, currency, isForex);
    },
  },
  {
    field: 'link',
    headerName: 'Link',
    sortable: false,
    filterable: false,
    width: 100,
    renderCell: (params: GridRenderCellParams) => {
      return <DateExplorerLink bankTransactionId={params.row.id} />;
    },
  },
  {
    field: 'pairedLink',
    headerName: 'Paired entity',
    sortable: false,
    filterable: false,
    width: 100,
    renderCell: (params: GridRenderCellParams) => {
      const pairedId = params.row.matchedBankTransaction?.id;
      if (!pairedId) return null;
      return (
        <Tooltip
          title={
            <>
              <Stack spacing={2}>
                <Item>
                  Matched transaction: <DateExplorerLink bankTransactionId={pairedId} label={''} />
                </Item>
                <Item>Amount: ${params.row.matchedBankTransaction.amount}</Item>
                <Item>Currency: ${params.row.matchedBankTransaction.currency}</Item>
                <Item>Description: {params.row.matchedBankTransaction.description}</Item>
                <Item>Source: {params.row.matchedBankTransaction.source}</Item>
              </Stack>
            </>
          }
        >
          <IconButton>
            <OpenInNewIcon>
              <DateExplorerLink bankTransactionId={pairedId} label={''} />
            </OpenInNewIcon>
          </IconButton>
        </Tooltip>
      );
    },
  },
];
